import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
    Chip,
} from "@mui/material";

export default function ScoreEntry({
    testPoints,
    frequencies,
    scores,
    onSave,
    onChange,
    onComplete,
}) {
    const [scoreData, setScoreData] = useState(() => {
        if (scores && Object.keys(scores).length > 0) {
            return scores;
        }
        // Initialize score data structure
        const initial = {};
        frequencies.forEach((freq, freqIndex) => {
            testPoints.forEach((point) => {
                const key = `${freqIndex}-${point}`;
                initial[key] = {
                    testLevel: "",
                    shieldingEffectiveness: "",
                };
            });
        });
        return initial;
    });

    // Helper: ALL fields must be filled (including no-reference ones)
    const computeComplete = (data) => {
        return frequencies.every((freq, freqIndex) =>
            testPoints.every((point) => {
                const key = `${freqIndex}-${point}`;
                const entry = data[key];
                return (
                    entry &&
                    entry.testLevel !== "" &&
                    entry.testLevel !== null &&
                    entry.testLevel !== undefined
                );
            }),
        );
    };

    // On mount, notify parent of initial completion status
    useEffect(() => {
        if (onComplete) onComplete(computeComplete(scoreData));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTestLevelChange = (freqIndex, point, value) => {
        const key = `${freqIndex}-${point}`;
        const reference = parseFloat(frequencies[freqIndex].reference) || 0;

        let shieldingEffectiveness = "";
        if (value && value.trim() !== "") {
            const testLevel = parseFloat(value);
            if (!isNaN(testLevel)) {
                shieldingEffectiveness = (reference - testLevel).toFixed(0);
            }
        }

        const newScoreData = {
            ...scoreData,
            [key]: {
                testLevel: value,
                shieldingEffectiveness: shieldingEffectiveness,
            },
        };
        setScoreData(newScoreData);
        if (onChange) onChange(newScoreData);
        if (onComplete) onComplete(computeComplete(newScoreData));
    };

    const handleSave = () => {
        onSave(scoreData);
    };

    const isValid = () => {
        return Object.values(scoreData).every((data) => data.testLevel !== "");
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const getScoreColor = (score) => {
        const numScore = parseFloat(score);
        if (isNaN(numScore)) return { color: "default", customSx: {} };
        if (numScore >= 100) return { color: "success", customSx: {} };
        if (numScore >= 90) return { color: "warning", customSx: {} };
        if (numScore >= 80)
            return {
                color: "warning",
                customSx: {
                    borderColor: "orange",
                    color: "darkorange",
                    "&:hover": { backgroundColor: "rgba(255, 165, 0, 0.1)" },
                },
            };
        return { color: "error", customSx: {} };
    };

    const getScoreLabel = (score) => {
        const numScore = parseFloat(score);
        if (isNaN(numScore)) return "-";
        if (numScore >= 100) return `${score} dB`;
        if (numScore >= 90) return `${score} dB`;
        if (numScore >= 80) return `${score} dB`;
        return `${score} dB`;
    };

    // Mobile card layout
    const renderMobileLayout = () => (
        <Box>
            <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
                Enter test level scores. Shielding effectiveness will be
                calculated automatically (Reference - Test Level):
            </Typography>

            {frequencies.map((freq, freqIndex) => (
                <Card
                    key={freqIndex}
                    elevation={0}
                    sx={{ border: 1, borderColor: "divider", mb: 2 }}
                >
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {freq.frequency} MHz
                            {freq.antennaLength
                                ? ` | ${freq.antennaLength}`
                                : ""}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                        >
                            Reference: {freq.reference} dBμV
                        </Typography>

                        {testPoints.map((point) => {
                            const key = `${freqIndex}-${point}`;
                            const data = scoreData[key] || {
                                testLevel: "",
                                shieldingEffectiveness: "",
                            };

                            return (
                                <Box
                                    key={point}
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        backgroundColor: "action.hover",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                    >
                                        {point}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                display="block"
                                                gutterBottom
                                            >
                                                Test Level (dBμV)
                                            </Typography>
                                            <TextField
                                                type="number"
                                                size="small"
                                                fullWidth
                                                disabled={
                                                    !freq.reference &&
                                                    freq.reference !== 0
                                                }
                                                error={
                                                    !freq.reference &&
                                                    freq.reference !== 0
                                                }
                                                helperText={
                                                    !freq.reference &&
                                                    freq.reference !== 0
                                                        ? "No reference set"
                                                        : undefined
                                                }
                                                value={data.testLevel}
                                                onChange={(e) =>
                                                    handleTestLevelChange(
                                                        freqIndex,
                                                        point,
                                                        e.target.value,
                                                    )
                                                }
                                                inputProps={{ step: "0.1" }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            borderColor: data.shieldingEffectiveness
                                                                ? getScoreColor(
                                                                      data.shieldingEffectiveness,
                                                                  ).color ===
                                                                  "success"
                                                                    ? "success.main"
                                                                    : getScoreColor(
                                                                          data.shieldingEffectiveness,
                                                                      )
                                                                          .color ===
                                                                      "error"
                                                                    ? "error.main"
                                                                    : parseFloat(
                                                                          data.shieldingEffectiveness,
                                                                      ) >= 90
                                                                    ? "warning.main"
                                                                    : "orange"
                                                                : undefined,
                                                            borderWidth: data.shieldingEffectiveness
                                                                ? 2
                                                                : 1,
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: data.shieldingEffectiveness
                                                                ? getScoreColor(
                                                                      data.shieldingEffectiveness,
                                                                  ).color ===
                                                                  "success"
                                                                    ? "success.main"
                                                                    : getScoreColor(
                                                                          data.shieldingEffectiveness,
                                                                      )
                                                                          .color ===
                                                                      "error"
                                                                    ? "error.main"
                                                                    : parseFloat(
                                                                          data.shieldingEffectiveness,
                                                                      ) >= 90
                                                                    ? "warning.main"
                                                                    : "orange"
                                                                : undefined,
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: data.shieldingEffectiveness
                                                                ? getScoreColor(
                                                                      data.shieldingEffectiveness,
                                                                  ).color ===
                                                                  "success"
                                                                    ? "success.main"
                                                                    : getScoreColor(
                                                                          data.shieldingEffectiveness,
                                                                      )
                                                                          .color ===
                                                                      "error"
                                                                    ? "error.main"
                                                                    : parseFloat(
                                                                          data.shieldingEffectiveness,
                                                                      ) >= 90
                                                                    ? "warning.main"
                                                                    : "orange"
                                                                : undefined,
                                                        },
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                display="block"
                                                gutterBottom
                                            >
                                                Shielding Effectiveness
                                            </Typography>
                                            {data.shieldingEffectiveness ? (
                                                <Chip
                                                    label={getScoreLabel(
                                                        data.shieldingEffectiveness,
                                                    )}
                                                    color={
                                                        getScoreColor(
                                                            data.shieldingEffectiveness,
                                                        ).color
                                                    }
                                                    variant="outlined"
                                                    sx={{
                                                        mt: 0.5,
                                                        ...getScoreColor(
                                                            data.shieldingEffectiveness,
                                                        ).customSx,
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="h6">
                                                    -
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            );
                        })}
                    </CardContent>
                </Card>
            ))}
        </Box>
    );

    // Desktop table layout
    const renderDesktopLayout = () => (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 0.5, sm: 1 },
                mb: { xs: 1, sm: 1.5 },
                overflowX: "auto",
            }}
        >
            <Typography
                variant="body1"
                gutterBottom
                sx={{ px: { xs: 0.5, sm: 0 } }}
            >
                Enter test level scores. Shielding effectiveness will be
                calculated automatically (Reference - Test Level):
            </Typography>

            <TableContainer sx={{ mt: 2 }}>
                <Table size="small" sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>Frequency | Antenna</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Reference (dBμV)</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Test Point</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Test Level (dBμV)</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Shielding Effectiveness (dB)</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {frequencies.map((freq, freqIndex) =>
                            testPoints.map((point, pointIndex) => {
                                const key = `${freqIndex}-${point}`;
                                const data = scoreData[key] || {
                                    testLevel: "",
                                    shieldingEffectiveness: "",
                                };

                                return (
                                    <TableRow key={key}>
                                        {pointIndex === 0 && (
                                            <TableCell
                                                rowSpan={testPoints.length}
                                            >
                                                {freq.frequency}
                                                {freq.antennaLength
                                                    ? ` MHz | ${freq.antennaLength}`
                                                    : " MHz"}
                                            </TableCell>
                                        )}
                                        {pointIndex === 0 && (
                                            <TableCell
                                                rowSpan={testPoints.length}
                                            >
                                                {freq.reference}
                                            </TableCell>
                                        )}
                                        <TableCell>{point}</TableCell>
                                        <TableCell>
                                            <TextField
                                                value={data.testLevel}
                                                disabled={
                                                    !freq.reference &&
                                                    freq.reference !== 0
                                                }
                                                placeholder={
                                                    !freq.reference &&
                                                    freq.reference !== 0
                                                        ? "No ref"
                                                        : undefined
                                                }
                                                title={
                                                    !freq.reference &&
                                                    freq.reference !== 0
                                                        ? "No reference set for this frequency"
                                                        : undefined
                                                }
                                                onChange={(e) =>
                                                    handleTestLevelChange(
                                                        freqIndex,
                                                        point,
                                                        e.target.value,
                                                    )
                                                }
                                                size="small"
                                                type="number"
                                                sx={{
                                                    width: 100,
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            borderColor: data.shieldingEffectiveness
                                                                ? getScoreColor(
                                                                      data.shieldingEffectiveness,
                                                                  ).color ===
                                                                  "success"
                                                                    ? "success.main"
                                                                    : getScoreColor(
                                                                          data.shieldingEffectiveness,
                                                                      )
                                                                          .color ===
                                                                      "error"
                                                                    ? "error.main"
                                                                    : parseFloat(
                                                                          data.shieldingEffectiveness,
                                                                      ) >= 90
                                                                    ? "warning.main"
                                                                    : "orange"
                                                                : undefined,
                                                            borderWidth: data.shieldingEffectiveness
                                                                ? 2
                                                                : 1,
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: data.shieldingEffectiveness
                                                                ? getScoreColor(
                                                                      data.shieldingEffectiveness,
                                                                  ).color ===
                                                                  "success"
                                                                    ? "success.main"
                                                                    : getScoreColor(
                                                                          data.shieldingEffectiveness,
                                                                      )
                                                                          .color ===
                                                                      "error"
                                                                    ? "error.main"
                                                                    : parseFloat(
                                                                          data.shieldingEffectiveness,
                                                                      ) >= 90
                                                                    ? "warning.main"
                                                                    : "orange"
                                                                : undefined,
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: data.shieldingEffectiveness
                                                                ? getScoreColor(
                                                                      data.shieldingEffectiveness,
                                                                  ).color ===
                                                                  "success"
                                                                    ? "success.main"
                                                                    : getScoreColor(
                                                                          data.shieldingEffectiveness,
                                                                      )
                                                                          .color ===
                                                                      "error"
                                                                    ? "error.main"
                                                                    : parseFloat(
                                                                          data.shieldingEffectiveness,
                                                                      ) >= 90
                                                                    ? "warning.main"
                                                                    : "orange"
                                                                : undefined,
                                                        },
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {data.shieldingEffectiveness ? (
                                                <Chip
                                                    label={getScoreLabel(
                                                        data.shieldingEffectiveness,
                                                    )}
                                                    color={
                                                        getScoreColor(
                                                            data.shieldingEffectiveness,
                                                        ).color
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    sx={
                                                        getScoreColor(
                                                            data.shieldingEffectiveness,
                                                        ).customSx
                                                    }
                                                />
                                            ) : (
                                                <span>-</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            }),
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    return <Box>{isMobile ? renderMobileLayout() : renderDesktopLayout()}</Box>;
}
